/**
 * Call Dispo Options — Situational Awareness Layer helper.
 * ────────────────────────────────────────────────────────
 * Given an issue scenario, returns the linear dropdown options for the
 * post-call dispo form. Always includes an "Other" escape hatch so the
 * CSR can type free-form context when reality doesn't fit the menu.
 *
 * Companion to:
 *   - resolveIssueScenario (situational/requiredInputs.ts)
 *   - getAvailableActions  (situational/availableActions.ts)
 *
 * Per mem://architecture/situational-awareness-layer: do NOT inline these
 * options anywhere else. Extend this helper instead.
 */
import type { IssueScenario } from "./requiredInputs";
import type { IssueStatus } from "../types";

export interface CallOutcomeOption {
  value: string;
  label: string;
  /** Suggests the resolution direction so the form can pre-select Step 2. */
  suggestsDirection?: ResolutionDirection;
}

export type ResolutionDirection =
  | "resolved_on_call"
  | "action_assigned"
  | "awaiting_external"
  | "escalate"
  | "no_progress";

export interface ResolutionDirectionOption {
  value: ResolutionDirection;
  label: string;
  /** Deterministic status mapping — drives the issue status on save. */
  status: IssueStatus;
  /** Short helper rendered under the option. */
  helper: string;
}

/* ───── Resolution direction → status (deterministic) ───── */

export const RESOLUTION_DIRECTIONS: ReadonlyArray<ResolutionDirectionOption> = [
  {
    value: "resolved_on_call",
    label: "Resolved on the call",
    status: "Resolved",
    helper: "Issue is fully closed. No further action required.",
  },
  {
    value: "action_assigned",
    label: "Action needed — I'll handle it",
    status: "In Progress",
    helper: "You took ownership of the next step (order part, dispatch crew, etc.).",
  },
  {
    value: "awaiting_external",
    label: "Awaiting external party",
    status: "In Progress",
    helper: "Customer, vendor, or municipality must act before this can close.",
  },
  {
    value: "escalate",
    label: "Escalate",
    status: "Open",
    helper: "Hand off to a manager or specialist — flagged for follow-up.",
  },
  {
    value: "no_progress",
    label: "No progress — try again",
    status: "Open",
    helper: "Couldn't reach anyone or got blocked. Schedule another attempt.",
  },
];

/* ───── Scenario → outcome menu ───── */

const SCHEDULING: ReadonlyArray<CallOutcomeOption> = [
  { value: "confirmed_datetime", label: "Confirmed new date/time", suggestsDirection: "resolved_on_call" },
  { value: "customer_reschedule", label: "Customer requested reschedule", suggestsDirection: "action_assigned" },
  { value: "left_voicemail", label: "No answer — left voicemail", suggestsDirection: "no_progress" },
  { value: "no_answer", label: "No answer — no voicemail", suggestsDirection: "no_progress" },
  { value: "callback_requested", label: "Customer requested callback", suggestsDirection: "awaiting_external" },
  { value: "other", label: "Other (type below)" },
];

const MATERIAL: ReadonlyArray<CallOutcomeOption> = [
  { value: "vendor_confirmed_eta", label: "Confirmed ETA with vendor", suggestsDirection: "awaiting_external" },
  { value: "vendor_escalated", label: "Vendor escalated to manager", suggestsDirection: "awaiting_external" },
  { value: "vendor_unreachable", label: "Vendor unreachable — left voicemail", suggestsDirection: "no_progress" },
  { value: "sourced_replacement", label: "Sourced replacement", suggestsDirection: "action_assigned" },
  { value: "customer_accepts_eta", label: "Customer accepts revised ETA", suggestsDirection: "resolved_on_call" },
  { value: "customer_declines_eta", label: "Customer declined revised ETA", suggestsDirection: "escalate" },
  { value: "other", label: "Other (type below)" },
];

const ACCESS: ReadonlyArray<CallOutcomeOption> = [
  { value: "entry_plan_confirmed", label: "Confirmed entry plan with customer", suggestsDirection: "resolved_on_call" },
  { value: "customer_on_site", label: "Customer will be on-site", suggestsDirection: "resolved_on_call" },
  { value: "captured_access_code", label: "Lockbox / gate code captured", suggestsDirection: "resolved_on_call" },
  { value: "rescheduled_access", label: "Customer rescheduled access", suggestsDirection: "action_assigned" },
  { value: "no_answer", label: "No answer", suggestsDirection: "no_progress" },
  { value: "other", label: "Other (type below)" },
];

const SERVICE: ReadonlyArray<CallOutcomeOption> = [
  { value: "diagnosed_on_call", label: "Diagnosed over the phone", suggestsDirection: "action_assigned" },
  { value: "service_visit_scheduled", label: "Service visit scheduled", suggestsDirection: "action_assigned" },
  { value: "customer_accepted_resolution", label: "Customer accepted resolution", suggestsDirection: "resolved_on_call" },
  { value: "escalated_qa", label: "Escalated to QA / install lead", suggestsDirection: "escalate" },
  { value: "left_voicemail", label: "No answer — left voicemail", suggestsDirection: "no_progress" },
  { value: "other", label: "Other (type below)" },
];

const PERMIT: ReadonlyArray<CallOutcomeOption> = [
  { value: "spoke_with_municipality", label: "Spoke with municipality", suggestsDirection: "awaiting_external" },
  { value: "permit_clarification_received", label: "Permit clarification received", suggestsDirection: "action_assigned" },
  { value: "awaiting_municipal_callback", label: "Awaiting municipal callback", suggestsDirection: "awaiting_external" },
  { value: "customer_signed_required_doc", label: "Customer signed required doc", suggestsDirection: "resolved_on_call" },
  { value: "no_answer", label: "No answer", suggestsDirection: "no_progress" },
  { value: "other", label: "Other (type below)" },
];

const GENERIC: ReadonlyArray<CallOutcomeOption> = [
  { value: "reached_customer", label: "Reached customer", suggestsDirection: "action_assigned" },
  { value: "left_voicemail", label: "Left voicemail", suggestsDirection: "no_progress" },
  { value: "no_answer", label: "No answer", suggestsDirection: "no_progress" },
  { value: "callback_requested", label: "Customer requested callback", suggestsDirection: "awaiting_external" },
  { value: "other", label: "Other (type below)" },
];

export function getCallOutcomeOptions(
  scenario: IssueScenario,
): ReadonlyArray<CallOutcomeOption> {
  switch (scenario) {
    case "scheduling": return SCHEDULING;
    case "material":   return MATERIAL;
    case "access":     return ACCESS;
    case "service":    return SERVICE;
    case "permit":     return PERMIT;
    default:           return GENERIC;
  }
}

/** Lookup a direction by value — handy for status mapping at save time. */
export function getDirectionByValue(value: ResolutionDirection): ResolutionDirectionOption {
  return RESOLUTION_DIRECTIONS.find((d) => d.value === value) ?? RESOLUTION_DIRECTIONS[3]!;
}

/** Lookup an outcome label for summary lines. */
export function getOutcomeLabel(
  scenario: IssueScenario,
  value: string,
): string {
  const opt = getCallOutcomeOptions(scenario).find((o) => o.value === value);
  return opt?.label ?? value;
}